import {
  ElementNode,
  LexicalNode,
  SerializedElementNode,
  $createParagraphNode,
  $isParagraphNode,
  RangeSelection,
  EditorConfig,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalEditor,
} from 'lexical';

export type SerializedSpoilerNode = SerializedElementNode;

/**
 * Lexical node for spoiler/collapsible content blocks.
 * Renders as <div class="spoilers"> in HTML.
 */
export class SpoilerNode extends ElementNode {
  static getType(): string {
    return 'spoiler';
  }

  static clone(node: SpoilerNode): SpoilerNode {
    return new SpoilerNode(node.__key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = document.createElement('div');
    element.className = 'spoilers';
    return element;
  }

  updateDOM(): boolean {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (node: HTMLElement) => {
        // `spoilers` is the canonical wrapper class produced by Lexical
        // export and `wrapSpoilerTags`; `spoiler` is the legacy class
        // emitted by the markdown-it `:::spoiler` container, accepted here
        // so old posts and `:::spoiler`-syntax markdown round-trip too.
        if (node.classList.contains('spoilers') || node.classList.contains('spoiler')) {
          return {
            conversion: convertSpoilerElement,
            priority: 1,
          };
        }
        return null;
      },
    };
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement('div');
    element.className = 'spoilers';
    return { element };
  }

  static importJSON(serializedNode: SerializedSpoilerNode): SpoilerNode {
    return $createSpoilerNode();
  }

  exportJSON(): SerializedSpoilerNode {
    return {
      ...super.exportJSON(),
      type: 'spoiler',
      version: 1,
    };
  }

  // Spoiler blocks can contain any block-level content
  canBeEmpty(): boolean {
    return false;
  }

  // Insert paragraph if empty
  collapseAtStart(selection: RangeSelection): boolean {
    const children = this.getChildren();
    if (children.length === 1 && $isParagraphNode(children[0])) {
      this.replace(children[0]);
      return true;
    }
    const paragraph = $createParagraphNode();
    children.forEach((child) => paragraph.append(child));
    this.replace(paragraph);
    return true;
  }
}

function convertSpoilerElement(): DOMConversionOutput {
  return { node: $createSpoilerNode() };
}

export function $createSpoilerNode(): SpoilerNode {
  return new SpoilerNode();
}

export function $isSpoilerNode(node: LexicalNode | null | undefined): node is SpoilerNode {
  return node instanceof SpoilerNode;
}

